<script>
  import { listObjects, uploadFile, signedUrl } from '../lib/aws.js'
  import { checkVideo } from '../lib/video_checks.js'
  import { postJSON } from '../lib/csrf.js'

  // Rooted at a fixed bucket + prefix; there is no bucket listing.
  // Override via data-props, e.g.
  //   data-props='{"bucket":"coghealth","root":"uploads/","checks":{"maxSizeMB":500}}'
  let { bucket = 'coghealth', root = 'uploads/', checks = {} } = $props()

  // Normalize the root prefix to end with "/" (unless empty).
  const base = root && !root.endsWith('/') ? `${root}/` : root

  let path = $state(base)
  let prefixes = $state([])
  let objects = $state([])
  let error = $state(null)
  let loading = $state(false)
  let fileInput

  // Upload/verify state machine: idle | checking | uploading | done
  let phase = $state('idle')
  let md5Progress = $state(0)
  let report = $state(null) // { status, filename, failureReasons, durationSeconds, ... }

  async function guard(fn) {
    error = null
    loading = true
    try { await fn() } catch (e) { error = e.message || String(e) } finally { loading = false }
  }

  function browse() {
    return guard(async () => {
      const res = await listObjects(bucket, path)
      prefixes = res.prefixes
      objects = res.objects
    })
  }

  function enter(p) { path = p; return browse() }

  const atRoot = $derived(path === base)
  const busy = $derived(phase === 'checking' || phase === 'uploading')

  function up() {
    if (atRoot) return
    const parts = path.replace(/\/$/, '').split('/')
    parts.pop()
    const parent = parts.length ? parts.join('/') + '/' : ''
    path = parent.length < base.length ? base : parent
    return browse()
  }

  async function onUpload(event) {
    const file = event.target.files?.[0]
    if (!file) return
    if (fileInput) fileInput.value = ''
    error = null
    report = null
    md5Progress = 0

    // 1. Automatic checks.
    phase = 'checking'
    let result
    try {
      result = await checkVideo(file, { ...checks, onMd5Progress: (p) => (md5Progress = p) })
    } catch (e) {
      error = e.message || String(e)
      phase = 'idle'
      return
    }

    const key = path + file.name

    // 2. Upload to S3 only if the checks passed.
    if (result.passed) {
      phase = 'uploading'
      try {
        await uploadFile(bucket, key, file)
      } catch (e) {
        result.passed = false
        result.failureReasons = [`Upload to S3 failed: ${e.message || e}`]
      }
    }

    const status = result.passed ? 'passed' : 'failed'

    // 3. Record the attempt (pass or fail) server-side.
    try {
      await postJSON('/uploads', {
        upload: {
          bucket, key,
          filename: result.filename,
          content_type: result.contentType,
          byte_size: result.byteSize,
          duration_seconds: result.durationSeconds,
          md5: result.md5,
          codec: result.codec,
          status,
          failure_reasons: result.failureReasons,
        },
      })
    } catch (e) {
      console.error('Failed to record upload attempt', e)
    }

    report = { ...result, status }
    phase = 'done'
    if (status === 'passed') await browse()
  }

  async function download(key) {
    await guard(async () => {
      const url = await signedUrl(bucket, key)
      window.open(url, '_blank')
    })
  }

  const fmtSize = (n) =>
    n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`
  const fmtDuration = (s) => {
    if (s == null) return '—'
    const m = Math.floor(s / 60), sec = Math.round(s % 60)
    return `${m}:${String(sec).padStart(2, '0')}`
  }
  const basename = (k) => k.replace(path, '')

  browse()
</script>

<div class="max-w-2xl mx-auto">
  {#if error}
    <div class="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>
  {/if}

  <div class="mb-3 text-sm font-mono text-gray-600 break-all">
    s3://{bucket}/{path}
  </div>

  <div class="flex items-center justify-between mb-3">
    <div class="flex gap-2">
      <button class="text-sm rounded-md border px-2 py-1 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              onclick={up} disabled={atRoot || busy}>⬆ Up</button>
      <button class="text-sm rounded-md border px-2 py-1 hover:bg-gray-50 disabled:opacity-40" onclick={browse} disabled={busy}>Refresh</button>
    </div>
    <label class="text-sm rounded-md bg-indigo-600 text-white px-3 py-1 hover:bg-indigo-500 {busy ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}">
      {#if phase === 'checking'}Checking… {Math.round(md5Progress * 100)}%
      {:else if phase === 'uploading'}Uploading…
      {:else}Upload video{/if}
      <input class="hidden" type="file" accept="video/*" bind:this={fileInput} onchange={onUpload} disabled={busy} />
    </label>
  </div>

  {#if report}
    {#if report.status === 'passed'}
      <div class="mb-4 rounded-md bg-green-50 ring-1 ring-green-200 p-4">
        <p class="font-semibold text-green-800">✓ “{report.filename}” uploaded successfully.</p>
        <dl class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-green-900">
          <div><dt class="inline text-green-700">Size:</dt> {fmtSize(report.byteSize)}</div>
          <div><dt class="inline text-green-700">Duration:</dt> {fmtDuration(report.durationSeconds)}</div>
          <div><dt class="inline text-green-700">Format:</dt> {report.codec || '—'}</div>
          <div class="truncate"><dt class="inline text-green-700">MD5:</dt> <span class="font-mono">{report.md5 || '—'}</span></div>
        </dl>
      </div>
    {:else}
      <div class="mb-4 rounded-md bg-red-50 ring-1 ring-red-200 p-4">
        <p class="font-semibold text-red-800">✗ Upload failed. Please try again.</p>
        <ul class="mt-2 list-disc list-inside text-sm text-red-900">
          {#each report.failureReasons as reason}<li>{reason}</li>{/each}
        </ul>
      </div>
    {/if}
  {/if}

  {#if loading}
    <p class="text-gray-500">Loading…</p>
  {:else}
    <ul class="divide-y border rounded-md">
      {#each prefixes as p}
        <li>
          <button class="w-full text-left px-3 py-2 hover:bg-gray-50" onclick={() => enter(p)}>
            📁 {p.replace(path, '')}
          </button>
        </li>
      {/each}
      {#each objects.filter((o) => o.Key !== path) as o}
        <li class="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
          <button class="text-left text-indigo-600 hover:text-indigo-500" onclick={() => download(o.Key)}>
            📄 {basename(o.Key)}
          </button>
          <span class="text-xs text-gray-500">{fmtSize(o.Size)}</span>
        </li>
      {/each}
      {#if prefixes.length === 0 && objects.filter((o) => o.Key !== path).length === 0}
        <li class="px-3 py-2 text-gray-500">Empty.</li>
      {/if}
    </ul>
  {/if}
</div>
